<template>
  <transition name="fade">
    <div v-if="show" class="fixed inset-0 z-50 bg-black bg-opacity-60 flex items-center justify-center px-4"
      @click.self="close">
      <div class="bg-white dark:bg-gray-900 rounded-lg shadow-2xl w-full max-w-2xl p-6 relative">
        <!-- Modal Header -->
        <div class="flex items-center justify-between mb-6 border-b border-gray-200 dark:border-gray-700 pb-3">
          <h2 class="text-2xl font-bold text-gray-800 dark:text-white">{{ title }}</h2>
          <button @click="close" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <svg class="h-6 w-6" fill="none" stroke="currentColor" stroke-width="2"
              viewBox="0 0 24 24" stroke-linecap="round" stroke-linejoin="round">
              <path d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <!-- Search -->
        <div class="mb-4">
          <input
            v-model="searchQuery"
            @input="debouncedFetchItems"
            type="text"
            placeholder="Search..."
            class="w-full px-4 py-3 rounded-md border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:outline-none"
          />
        </div>

        <!-- Loading -->
        <div v-if="loading" class="text-center py-10">
          <svg aria-hidden="true" class="w-10 h-10 mx-auto text-gray-300 animate-spin dark:text-gray-600 fill-blue-600" viewBox="0 0 100 101" fill="none">
            <path d="M100 50.5908C100 78.2051 77.6142 100.591..." fill="currentColor"/>
            <path d="M93.9676 39.0409C96.393 38.4038..." fill="currentFill"/>
          </svg>
          <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">Loading...</p>
        </div>

        <!-- Items -->
        <div v-else-if="items && items.length > 0" class="max-h-64 overflow-y-auto rounded border border-gray-200 dark:border-gray-700">
          <table class="min-w-full text-sm divide-y divide-gray-200 dark:divide-gray-700">
            <thead class="bg-gray-100 dark:bg-gray-800 sticky top-0 z-10">
              <tr>
                <th class="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300">Select</th>
                <th class="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300">{{ getLabelHeader() }}</th>
                <th v-if="itemsEndpoint === 'users'" class="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300">Email</th>
              </tr>
            </thead>
            <tbody class="bg-white dark:bg-gray-900 divide-y divide-gray-100 dark:divide-gray-700">
              <tr v-for="item in items" :key="item.id" class="hover:bg-gray-50 dark:hover:bg-gray-800">
                <td class="px-4 py-3">
                  <input type="checkbox" :value="item.id" v-model="selectedItems"
                    class="rounded text-primary-600 border-gray-300 focus:ring-primary-500" />
                </td>
                <td class="px-4 py-3 text-gray-800 dark:text-gray-100">
                  {{ getItemLabel(item) }}
                </td>
                <td v-if="itemsEndpoint === 'users'" class="px-4 py-3 text-gray-600 dark:text-gray-400">
                  {{ item.email }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- No Results -->
        <p v-else class="text-center text-gray-500 dark:text-gray-400 mt-4">
          No results found.
        </p>

        <!-- Actions -->
        <div class="mt-6 flex justify-end gap-3 border-t border-gray-200 dark:border-gray-700 pt-4">
          <button @click="close"
            class="px-5 py-2 rounded-md bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600 transition">
            Cancel
          </button>
          <button @click="addItems"
            class="px-5 py-2 rounded-md bg-primary-700 text-white hover:bg-primary-800 transition">
            Add
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import axios from "axios";

export default {
  props: {
    show: Boolean,
    title: String,
    itemsEndpoint: String, // 'users', 'courses', 'branches', or 'categories'
    groupItems: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      items: [],
      selectedItems: [],
      searchQuery: "",
      loading: false,
      debouncedFetchItems: null,
    };
  },
watch: {
  show(newVal) {
    if (newVal) {
      this.searchQuery = '';
      this.selectedItems = [];
      this.items = [];
      this.fetchItems();
    }
  },
},

created() {
  this.debouncedFetchItems = this.debounce(this.fetchItems, 400);
},
  methods: {
debounce(func, wait) {
  let timeout;
  return function (...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
},

    getLabelHeader() {
      switch (this.itemsEndpoint) {
        case 'users': return 'Name';
        case 'courses': return 'Course Title';
        case 'branches': return 'Branch Name';
        case 'categories': return 'Category Name';
        default: return 'Label';
      }
    },
    getItemLabel(item) {
      if (this.itemsEndpoint === 'users') return `${item.first_name} ${item.last_name}`;
      return item.title || item.name;
    },

    async fetchItems() {
      this.loading = true;

      let query = '';
      let variables = {};

      if (this.itemsEndpoint === 'users') {
        query = `
          query($search: String) {
            users(search: $search) {
              id
              first_name
              last_name
              email
            }
          }
        `;
        variables = { search: this.searchQuery };
      } else if (this.itemsEndpoint === 'courses') {
        query = `
          query($search: String) {
            courses(search: $search) {
              id
              title
            }
          }
        `;
        variables = { search: this.searchQuery };
      } else if (this.itemsEndpoint === 'branches') {
        query = `
          query($search: String) {
            branches(search: $search) {
              id
              name
            }
          }
        `;
          variables = { search: this.searchQuery };
      } else if (this.itemsEndpoint === 'categories') {
        query = `
          query($search: String) {
            categories(search: $search) {
              id
              name
            }
          }
        `;
        variables = { search: this.searchQuery };
      }

      try {
        const response = await axios.post(`${process.env.VUE_APP_API_URL}/graphql`, {
          query,
          variables,
        });

        const fetchedItems = response.data.data[this.itemsEndpoint];

        this.items = fetchedItems.filter(item =>
          !this.groupItems.some(groupItem => groupItem.id === item.id)
        );
      } catch (error) {
        console.error(`Error fetching ${this.itemsEndpoint}:`, error);
      } finally {
        this.loading = false;
      }
    },

    async addItems() {
      const mutationMap = {
        users: `
          mutation ($user_id: Int!, $group_ids: [Int!]!) {
            addUserToGroup(user_id: $user_id, group_ids: $group_ids) {
              id
              name
            }
          }
        `,
        courses: `
          mutation ($group_id: Int!, $course_ids: [Int!]!) {
            addCoursesToGroup(group_id: $group_id, course_ids: $course_ids) {
              id
              courses {
                id
                title
              }
            }
          }
        `,
        branches: `
          mutation ($group_id: Int!, $branch_ids: [Int!]!) {
            addBranchesToGroup(group_id: $group_id, branch_ids: $branch_ids) {
              id
              branches {
                id
                name
              }
            }
          }
        `,
        categories: `
          mutation ($group_id: Int!, $category_ids: [Int!]!) {
            addCategoriesToGroup(group_id: $group_id, category_ids: $category_ids) {
              id
              categories {
                id
                name
              }
            }
          }
        `
      };

      const variableKeyMap = {
        courses: 'course_ids',
        branches: 'branch_ids',
        categories: 'category_ids',
      };

      const groupId = parseInt(this.$parent.group.id);

      try {
        if (this.itemsEndpoint === 'users') {
          await Promise.all(
            this.selectedItems.map((userId) =>
              axios.post(`${process.env.VUE_APP_API_URL}/graphql`, {
                query: mutationMap.users,
                variables: {
                  group_ids: [groupId],
                  user_id: parseInt(userId),
                },
              })
            )
          );
        } else {
          const variableKey = variableKeyMap[this.itemsEndpoint];
          await axios.post(`${process.env.VUE_APP_API_URL}/graphql`, {
            query: mutationMap[this.itemsEndpoint],
            variables: {
              group_id: groupId,
              [variableKey]: this.selectedItems.map(Number),
            },
          });
        }

        this.$emit("refreshGroupData");
        this.close();
      } catch (error) {
        console.error(`Error adding ${this.itemsEndpoint} to group:`, error);
      }
    },

    close() {
      this.selectedItems = [];
      this.$emit("close");
    },
  },
};
</script>
<style scoped>
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.25s ease;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

</style>